// CampanasTab — SERIE «Campañas de promoción», tanda 3 (pantalla). Componente
// PROPIO, aparte de PromocionesTab (aplicación suelta). Flujo:
//   crear (picker categoría/marca por id, o catálogo) → previsualizar (tabla +
//   Excel) → aplicar (2.º clic) → historial con estado y COBERTURA (qué campaña
//   cubre cada variación; conflictos declarados) → detalle de campaña activa con
//   DRIFT TRI-ESTADO → quitar con el diálogo de modificadas por fuera.
// Contrato y asserts en campanasTab.test.js.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from './ui/Button';
import { MENSAJE_SOLO_LECTURA } from '../utils/soloLectura';
import { exportCampanaPreviewToExcel } from '../utils/exportExcel';
import './CampanasTab.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function CampanasTab({ soloLectura = false }) {
  const [categorias, setCategorias] = useState([]);
  const [modo, setModo] = useState('categoria'); // 'categoria' | 'catalogo'
  const [selCat, setSelCat] = useState(new Set());
  const [busqueda, setBusqueda] = useState('');
  const [productos, setProductos] = useState([]);
  const [selProd, setSelProd] = useState(new Set());

  const [nombre, setNombre] = useState('');
  const [pct, setPct] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  const [preview, setPreview] = useState(null);
  const [cargandoPreview, setCargandoPreview] = useState(false);
  const [confirmando, setConfirmando] = useState(false); // 2.º clic de Aplicar
  const [desactualizada, setDesactualizada] = useState(false); // la preview mostrada ya no refleja los datos actuales
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');

  const [historial, setHistorial] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [detalleCargando, setDetalleCargando] = useState(null); // id de la campaña cuyo detalle se está leyendo
  const [detalleError, setDetalleError] = useState('');
  const detalleRef = useRef(null);
  const [quitarOpen, setQuitarOpen] = useState(false);
  const [restaurar, setRestaurar] = useState(new Set());
  const [quitando, setQuitando] = useState(false);

  const cargarCategorias = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/promociones/categorias`);
      const d = await r.json();
      if (r.ok) setCategorias(d.categorias || []);
    } catch (_) { /* el picker no bloquea la pantalla */ }
  }, []);
  const cargarHistorial = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/promociones/campanas`);
      const d = await r.json();
      if (r.ok) setHistorial(d);
    } catch (_) { /* el historial no bloquea la pantalla */ }
  }, []);
  useEffect(() => { cargarCategorias(); cargarHistorial(); }, [cargarCategorias, cargarHistorial]);

  const toggle = (set, val) => { const s = new Set(set); if (s.has(val)) s.delete(val); else s.add(val); return s; };

  const criterioActual = () => (modo === 'categoria'
    ? { tipo: 'categoria', valores: [...selCat] }
    : { tipo: 'productos', valores: [...selProd] });

  // La previsualización mostrada refleja unos datos concretos; si cambian la
  // SELECCIÓN (categoría/producto/modo), el % o las FECHAS, deja de ser válida:
  // se marca DESACTUALIZADA y Aplicar/Exportar quedan deshabilitados hasta
  // previsualizar de nuevo. Cambiar el NOMBRE no invalida (no afecta lo previsualizado).
  const marcarDesactualizada = () => { if (preview) { setDesactualizada(true); setConfirmando(false); } };

  // Limpiar: reset TOTAL al estado inicial (selección, nombre, %, fechas y toda la
  // previsualización con su estado). Deja el formulario en blanco, listo.
  const limpiar = () => {
    setPreview(null); setResultado(null); setError(''); setConfirmando(false); setDesactualizada(false);
    setSelCat(new Set()); setSelProd(new Set()); setNombre(''); setPct(''); setFechaInicio(''); setFechaFin('');
  };

  const buscarProductos = async () => {
    try {
      const r = await fetch(`${API_URL}/api/products?search=${encodeURIComponent(busqueda)}&status=publish&per_page=20`);
      const d = await r.json();
      // Campañas soportan variables (por variación) Y simples (por producto): los dos
      // entran en la búsqueda por nombre, igual que ya entran por categoría. Se excluyen
      // otros tipos (grouped/external), que no tienen precio propio que rebajar.
      if (r.ok) setProductos((d.products || []).filter((p) => p.type === 'variable' || p.type === 'simple'));
    } catch (_) { /* no bloquea */ }
  };

  const previsualizar = async () => {
    setError(''); setResultado(null); setConfirmando(false); setPreview(null);
    setCargandoPreview(true);
    try {
      const r = await fetch(`${API_URL}/api/promociones/campanas/previsualizar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pct: Number(pct), fechaInicio: fechaInicio || null, fechaFin: fechaFin || null, criterio: criterioActual() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'No se pudo previsualizar');
      setPreview(d);
      setDesactualizada(false); // la preview vuelve a reflejar los datos actuales
    } catch (e) {
      setError(e.message);
    } finally {
      setCargandoPreview(false);
    }
  };

  const aplicar = async () => {
    if (!confirmando) { setConfirmando(true); return; }
    setAplicando(true); setResultado(null); setError('');
    try {
      const r = await fetch(`${API_URL}/api/promociones/campanas/aplicar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre, pct: Number(pct), fechaInicio: fechaInicio || null, fechaFin: fechaFin || null, criterio: criterioActual() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'No se pudo aplicar la campaña');
      setResultado(d); setConfirmando(false); cargarHistorial();
    } catch (e) {
      setError(e.message);
    } finally {
      setAplicando(false);
    }
  };

  // La tarjeta del detalle se agrega DEBAJO del historial: sin esto el clic
  // parece no hacer nada (la pantalla no se mueve). jsdom no trae scrollIntoView.
  useEffect(() => {
    if (detalle && detalleRef.current && typeof detalleRef.current.scrollIntoView === 'function') {
      detalleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [detalle]);

  const verDetalle = async (id) => {
    if (detalleCargando) return; // ya hay una lectura en curso: no re-disparar
    setDetalle(null); setQuitarOpen(false); setDetalleError('');
    setDetalleCargando(id);
    try {
      const r = await fetch(`${API_URL}/api/promociones/campanas/${id}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `el servidor respondió ${r.status}`);
      setDetalle(d);
    } catch (e) {
      setDetalleError(`No se pudo cargar el detalle: ${e.message}. Prueba de nuevo.`);
    } finally {
      setDetalleCargando(null);
    }
  };

  const abrirQuitar = () => { setRestaurar(new Set(detalle.sin_cambios || [])); setQuitarOpen(true); };

  const confirmarQuitar = async () => {
    setQuitando(true);
    try {
      // Con drift no_disponible NO se sabe qué cambió: se restaura todo el previo
      // (el diálogo YA advirtió el riesgo). Con drift comprobado se manda la
      // elección (sin_cambios por default + las modificadas que el usuario tildó).
      const body = detalle.drift_estado === 'no_disponible' ? {} : { variacionesARestaurar: [...restaurar] };
      const r = await fetch(`${API_URL}/api/promociones/campanas/${detalle.id}/quitar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'No se pudo quitar la campaña');
      setQuitarOpen(false); setDetalle(null); cargarHistorial();
      setResultado({ quitada: true, restauradas: d.restauradas || [] });
    } catch (e) {
      setError(e.message);
    } finally {
      setQuitando(false);
    }
  };

  const conflictos = historial?.cobertura_activa?.conflictos || [];

  // Lo que la campaña VA A ESCRIBIR (los bloqueantes no se aplican): sirve para
  // que la confirmación del 2.º clic diga a qué se aplicará y con qué nombres.
  const aplicables = (preview?.filas || []).filter((f) => !f.bloqueante);
  const nombresProductos = [...new Set(aplicables.map((f) => f.producto).filter(Boolean))];

  return (
    <div className="camp-tab">
      {/* ── Crear ─────────────────────────────────────────────────────────── */}
      <section className="camp-card">
        <h3>Crear campaña</h3>
        <div className="camp-modo" role="group" aria-label="Cómo elegir los productos">
          <Button variant={modo === 'categoria' ? 'primary' : 'secondary'} size="sm" onClick={() => { setModo('categoria'); marcarDesactualizada(); }}>Por categoría o marca</Button>
          <Button variant={modo === 'catalogo' ? 'primary' : 'secondary'} size="sm" onClick={() => { setModo('catalogo'); marcarDesactualizada(); }}>Desde el catálogo</Button>
        </div>

        {modo === 'categoria' ? (
          <div className="camp-picker" aria-label="Categorías">
            {categorias.length === 0 ? <p className="camp-hint">Cargando categorías…</p>
              : categorias.map((c) => (
                <label key={c.id} className="camp-check">
                  <input type="checkbox" checked={selCat.has(c.id)} onChange={() => { setSelCat((s) => toggle(s, c.id)); marcarDesactualizada(); }} />
                  {c.name} ({c.count})
                </label>
              ))}
            <p className="camp-hint">Las «marcas» son categorías; la selección es por id. El alcance real lo confirma la previsualización.</p>
          </div>
        ) : (
          <div className="camp-picker">
            <div className="camp-buscar">
              <input aria-label="Buscar producto" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Nombre del producto" />
              <Button size="sm" onClick={buscarProductos}>Buscar</Button>
            </div>
            {productos.map((p) => (
              <label key={p.id} className="camp-check">
                <input type="checkbox" checked={selProd.has(p.id)} onChange={() => { setSelProd((s) => toggle(s, p.id)); marcarDesactualizada(); }} />
                {p.name}
              </label>
            ))}
          </div>
        )}

        <div className="camp-params">
          <label>Nombre<input aria-label="Nombre de la campaña" value={nombre} onChange={(e) => setNombre(e.target.value)} /></label>
          <label>% descuento<input aria-label="Porcentaje de descuento" type="number" value={pct} onChange={(e) => { setPct(e.target.value); marcarDesactualizada(); }} /></label>
          <label>Desde<input aria-label="Fecha de inicio" type="date" value={fechaInicio} onChange={(e) => { setFechaInicio(e.target.value); marcarDesactualizada(); }} /></label>
          <label>Hasta<input aria-label="Fecha de fin" type="date" value={fechaFin} onChange={(e) => { setFechaFin(e.target.value); marcarDesactualizada(); }} /></label>
        </div>
        <p className="camp-hint">Sin fechas, la oferta queda abierta (sin vencimiento). El precio se redondea hacia abajo a la centena.</p>

        {error && <p className="camp-error" role="alert">{error}</p>}

        <div className="camp-actions">
          <Button onClick={previsualizar} loading={cargandoPreview}>Previsualizar</Button>
          {preview && (
            <>
              <Button variant="secondary" onClick={() => exportCampanaPreviewToExcel(preview.filas)} disabled={desactualizada}>Exportar Excel</Button>
              <Button variant="primary" onClick={aplicar} loading={aplicando} disabled={desactualizada || soloLectura} title={soloLectura ? MENSAJE_SOLO_LECTURA : undefined}>
                {confirmando ? '¿Seguro? Aplicar campaña' : 'Aplicar campaña'}
              </Button>
              <Button variant="secondary" onClick={limpiar}>Limpiar</Button>
            </>
          )}
        </div>

        {preview && desactualizada && (
          <p className="camp-desactualizada" role="status">
            La previsualización quedó desactualizada: vuelve a previsualizar antes de aplicar.
          </p>
        )}
        {preview && confirmando && !desactualizada && (
          <p className="camp-confirmar" role="status">
            Se aplicará a {aplicables.length} {aplicables.length === 1 ? 'variación' : 'variaciones'}
            {nombresProductos.length ? ` de: ${nombresProductos.join(', ')}` : ''}. Revisa que no se haya colado ninguno antes de confirmar.
          </p>
        )}

        {preview && (
          <>
            <p className="camp-resumen">
              {preview.total} variaciones · {preview.ya_en_oferta} ya en oferta · {preview.con_bloqueo} con bloqueo
              {preview.en_otra_campana ? ` · ${preview.en_otra_campana} en otra campaña` : ''}
            </p>
            {preview.declaracion && (
              <p className="camp-hint">
                {preview.declaracion.productos_en_alcance} de {preview.declaracion.catalogo_total_productos} productos en alcance.
                {' '}{preview.declaracion.aviso}
              </p>
            )}
            <div className="camp-tabla" role="table" aria-label="Previsualización de la campaña">
              <div className="camp-fila camp-fila--head" role="row">
                <span>Producto</span><span>Variación</span><span>Categoría</span><span>Actual</span>
                <span>Regular</span><span>Promoción</span><span>% real</span><span>Alertas</span>
              </div>
              {preview.filas.map((f, i) => (
                <div key={`${f.variation_id}-${i}`} className={`camp-fila${f.bloqueante ? ' camp-fila--bloq' : ''}`} role="row">
                  <span>{f.producto || `#${f.variation_id}`}</span>
                  <span>{f.variacion || '—'}</span>
                  <span>{f.categoria || '—'}</span>
                  <span>{f.precio_actual != null ? f.precio_actual : '—'}</span>
                  <span>{f.precio_regular != null ? f.precio_regular : '—'}</span>
                  <span>{f.precio_promocion != null ? f.precio_promocion : '—'}</span>
                  <span>{f.pct_real != null ? `${f.pct_real}%` : '—'}</span>
                  <span>
                    {f.alertas && f.alertas.length ? f.alertas.join(' · ') : '—'}
                    {f.bloqueante && <strong className="camp-bloq"> — no se aplicará</strong>}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {resultado && !resultado.quitada && (
          <p className="camp-resultado" role="status">
            Campaña aplicada: {resultado.actualizadas.length} {resultado.actualizadas.length === 1 ? 'variación actualizada' : 'variaciones actualizadas'}
            {resultado.no_aplicadas && resultado.no_aplicadas.length ? `, ${resultado.no_aplicadas.length} no aplicada${resultado.no_aplicadas.length === 1 ? '' : 's'}` : ''}.
          </p>
        )}
        {resultado && resultado.quitada && (
          <p className="camp-resultado" role="status">Campaña quitada: {resultado.restauradas.length} variaciones restauradas.</p>
        )}
      </section>

      {/* ── Historial ─────────────────────────────────────────────────────── */}
      <section className="camp-card">
        <h3>Campañas</h3>
        {conflictos.length > 0 && (
          <div className="camp-conflicto" role="alert">
            Conflicto de cobertura: {conflictos.map((cf) => `variación ${cf.variation_id} en ${cf.campanas.map((x) => x.nombre).join(', ')}`).join(' · ')}
          </div>
        )}
        {historial === null ? <p className="camp-hint">Cargando…</p>
          : (historial.campanas || []).length === 0 ? <p className="camp-hint">Todavía no hay campañas.</p>
            : (
              <div className="camp-tabla" role="table" aria-label="Historial de campañas">
                <div className="camp-fila camp-fila--head" role="row">
                  <span>Nombre</span><span>%</span><span>Ventana</span><span>Estado</span><span>Cobertura</span><span />
                </div>
                {historial.campanas.map((c) => (
                  <div key={c.id} className="camp-fila" role="row">
                    <span>{c.nombre}</span>
                    <span>{c.pct}%</span>
                    <span>{c.fechaInicio || 'abierta'}{c.fechaFin ? ` → ${c.fechaFin}` : ''}</span>
                    <span>{c.estado}</span>
                    <span>cubre {c.variaciones.length} {c.variaciones.length === 1 ? 'variación' : 'variaciones'}</span>
                    <span><Button size="sm" aria-label={`Ver detalle de ${c.nombre}`} disabled={detalleCargando !== null} onClick={() => verDetalle(c.id)}>{detalleCargando === c.id ? 'Cargando…' : 'Ver detalle'}</Button></span>
                  </div>
                ))}
              </div>
            )}
        {detalleError && <p className="camp-warn" role="alert">{detalleError}</p>}
      </section>

      {/* ── Detalle de una campaña ────────────────────────────────────────── */}
      {detalle && (
        <section ref={detalleRef} className="camp-card" aria-label={`Detalle de ${detalle.nombre}`}>
          <h3>{detalle.nombre} <span className="camp-estado">({detalle.estado})</span></h3>
          <p className="camp-hint">{detalle.pct}% · {detalle.fechaInicio || 'abierta'}{detalle.fechaFin ? ` → ${detalle.fechaFin}` : ''} · cubre {detalle.variaciones.length} variaciones</p>
          {detalle.drift_estado === 'comprobado' ? (
            <p className="camp-drift">
              Estado comprobado: {detalle.modificadas_por_fuera.length} {detalle.modificadas_por_fuera.length === 1 ? 'modificada por fuera' : 'modificadas por fuera'}
              {' · '}{detalle.sin_cambios.length} sin cambios
            </p>
          ) : (
            <p className="camp-drift camp-drift--warn">No se pudo comprobar si hubo cambios desde que se aplicó.</p>
          )}
          {detalle.estado === 'activa' && <Button variant="destructive" onClick={abrirQuitar}>Quitar campaña</Button>}
        </section>
      )}

      {/* ── Diálogo de Quitar ─────────────────────────────────────────────── */}
      {quitarOpen && detalle && (
        <div className="camp-dialog" role="dialog" aria-label="Quitar campaña">
          <div className="camp-dialog-box">
            <h4>Quitar «{detalle.nombre}»</h4>
            {detalle.drift_estado === 'no_disponible' ? (
              <p className="camp-warn" role="alert">
                No se pudo comprobar si hubo cambios en WooCommerce. Restaurar podría pisar cambios hechos allí — revisa antes de confirmar.
              </p>
            ) : detalle.modificadas_por_fuera.length > 0 ? (
              <>
                <p>Estas variaciones cambiaron en WooCommerce desde que se aplicó la campaña. Elige cuáles restaurar; las que NO tildes se dejan como están (no se pisan):</p>
                {detalle.modificadas_por_fuera.map((m) => (
                  <label key={m.variation_id} className="camp-check">
                    <input
                      type="checkbox" checked={restaurar.has(m.variation_id)}
                      aria-label={`Restaurar variación ${m.variation_id}`}
                      onChange={() => setRestaurar((s) => toggle(s, m.variation_id))}
                    />
                    Variación {m.variation_id}: la campaña dejó {m.escrito?.sale_price}; ahora hay {m.actual?.sale_price}
                  </label>
                ))}
              </>
            ) : (
              <p>No hay cambios por fuera; se restaura el estado previo de todas las variaciones.</p>
            )}
            <div className="camp-actions">
              <Button variant="destructive" onClick={confirmarQuitar} loading={quitando}>Confirmar quitar</Button>
              <Button variant="secondary" onClick={() => setQuitarOpen(false)}>Cancelar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
